/**
 * Loads GA4 (early stub + deferred gtag.js) and Cloudflare Web Analytics after window load.
 * Env: `VITE_GA4_MEASUREMENT_ID`, `VITE_CF_WEB_ANALYTICS` (legacy `VITE_CF_WEB_ANALYTICS_TOKEN`).
 */

import { getViteMetaEnv } from '@/utils/viteMetaEnv';

type GtagCommand = unknown[];

type WindowWithGa4Stub = Window & {
  dataLayer?: GtagCommand[];
  gtag?: (...args: unknown[]) => void;
  __colorscalesGa4Configured?: boolean;
};

const GA4_PRECONNECTS = [
  "https://www.googletagmanager.com",
  "https://www.google-analytics.com",
] as const;

function appendPreconnectIfMissing(href: string): void {
  if (typeof document === "undefined") {
    return;
  }
  if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
    return;
  }
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = href;
  document.head.appendChild(link);
}

const PRODUCTION_HOST = "colorscales.io";

function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === PRODUCTION_HOST;
}

function onWindowLoad(fn: () => void): void {
  if (typeof window === "undefined") {
    return;
  }
  if (document.readyState === "complete") {
    fn();
  } else {
    window.addEventListener("load", fn, { once: true });
  }
}

/** GA4 `gtag('config', …)` options (privacy-first; `anonymize_ip` is UA-only and ignored for GA4). */
const GA4_CONFIG_BASE = {
  allow_google_signals: false,
  allow_ad_personalization_signals: false,
} as const;

function initGoogleAnalytics(): void {
  const measurementId = getViteMetaEnv().VITE_GA4_MEASUREMENT_ID?.trim();
  if (!measurementId || typeof window === "undefined" || !isProductionHost()) {
    return;
  }

  for (const origin of GA4_PRECONNECTS) {
    appendPreconnectIfMissing(origin);
  }

  const w = window as WindowWithGa4Stub;
  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }
  const dataLayer = w.dataLayer;
  if (typeof w.gtag !== "function") {
    // Match Google's inline stub exactly: queue Arguments objects, not arrays.
    // gtag.js ignores array-shaped entries, which breaks config/page_view delivery.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments as unknown as GtagCommand);
    };
  }

  if (!w.__colorscalesGa4Configured) {
    w.__colorscalesGa4Configured = true;
    w.gtag("js", new Date());
    w.gtag("config", measurementId, {
      ...GA4_CONFIG_BASE,
      send_page_view: true,
    });
  }

  // Defer only the external library so LCP is not contended; dataLayer + gtag stub + config
  // run immediately so `mirrorEventToGa4` can queue before `window` "load".
  onWindowLoad(() => {
    const existing = document.querySelector(
      `script[src^="https://www.googletagmanager.com/gtag/js?id="]`,
    );
    if (existing) {
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    if ("fetchPriority" in script) {
      script.fetchPriority = "low";
    }
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  });
}

function initCloudflareWebAnalytics(): void {
  if (!isProductionHost()) {
    return;
  }

  const env = getViteMetaEnv();
  const token =
    env.VITE_CF_WEB_ANALYTICS?.trim() ||
    env.VITE_CF_WEB_ANALYTICS_TOKEN?.trim();
  if (!token) {
    return;
  }

  onWindowLoad(() => {
    const script = document.createElement("script");
    script.defer = true;
    script.src = "https://static.cloudflareinsights.com/beacon.min.js";
    script.setAttribute("data-cf-beacon", JSON.stringify({ token }));
    document.body.appendChild(script);
  });
}

/** Register third-party scripts: GA4 (early dataLayer stub + deferred gtag.js), Cloudflare after load. */
export function registerThirdPartyScripts(): void {
  initGoogleAnalytics();
  initCloudflareWebAnalytics();
}

declare global {
  interface Window {
    dataLayer?: GtagCommand[];
    gtag?: (...args: unknown[]) => void;
  }
}
